import { ButtonIcon, Callout, Tooltip, VariableIcon } from 'canoe';
import React, { useCallback, useRef, useState } from 'react';
import { BaseRange, Editor, Location, Range } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';

import { VariableEditor } from 'craft/components/VariableEditor';
import { NO_ACTION } from 'craft/constants';
import { wrapVariable } from 'craft/rte/plugins/withVariable';
import { useViewportDom } from 'craft/state/viewport/selectors';

interface VariableButtonProps {
  disabled?: boolean;
}

export const VariableButton: React.FC<VariableButtonProps> = ({
  disabled,
}: VariableButtonProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [isToolbarVisible, setToolbarVisible] = useState(false);
  const [storedRange, setStoredRange] = useState<BaseRange>();
  const rteEditor = useSlate() as ReactEditor;
  const rteEditorRef = useRef(rteEditor);
  const { viewportWrapperEl } = useViewportDom();

  const range = rteEditor.selection || undefined;
  const hasRange = range && Range.isExpanded(range);
  rteEditorRef.current = rteEditor;

  let selectedText = '';
  if (rteEditor.selection && !Range.isCollapsed(rteEditor.selection)) {
    selectedText = Editor.string(rteEditor, rteEditor.selection as Location);
  }

  const closeToolbar = useCallback(() => {
    setToolbarVisible(false);
    setStoredRange(undefined);
  }, []);

  const toggleToolbar = () => {
    if (isToolbarVisible) {
      closeToolbar();
    } else {
      setStoredRange(range);
      setToolbarVisible(true);
    }
  };

  const addVariable = useCallback(
    ({ name, fallback }) => {
      wrapVariable(rteEditor, name, fallback, storedRange);
      closeToolbar();
    },
    [closeToolbar, rteEditor, storedRange],
  );

  const initialValue = selectedText;

  return (
    <>
      {isToolbarVisible && (
        <Callout
          referenceElement={ref.current}
          onClose={() => setToolbarVisible(false)}
          closeKey="Escape"
          placement="top-start"
          usePortal={false}
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <VariableEditor
                initialValue={{
                  name: initialValue,
                  fallback: initialValue,
                }}
                onClose={closeToolbar}
                onVariable={addVariable}
              />
            </div>
          )}
        </Callout>
      )}
      <Tooltip placement="top" offset="xxs" content="Variable">
        <ButtonIcon
          ref={ref}
          size="small"
          icon={VariableIcon}
          active={isToolbarVisible}
          disabled={!hasRange || disabled}
          onClick={() => window.requestAnimationFrame(toggleToolbar)}
          radius="xsmall"
          color="white"
        />
      </Tooltip>
    </>
  );
};
